import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, X, Users, User, Target, MessageSquare, Sparkles, Radio, BookOpen, ArrowRight, Check, FileDown, Copy, Trash2, Download, Layers, Hash, Film, Workflow, Activity } from 'lucide-react';
const STORAGE_KEY = 'mp-rhetoric-compass-form-v1';
type PointId = 'receiver' | 'context' | 'sender' | 'objective' | 'subject' | 'channel' | 'genre' | 'flow' | 'form';
type CompassPoint = {
  id: PointId;
  label: string;
  angle: number; // degrees, -90 = top
  color: string;
  softColor: string;
  icon: ComponentType<{
    className?: string;
  }>;
  intro: string;
  questions: string[];
};
const points: CompassPoint[] = [{
  id: 'context',
  label: 'Context',
  angle: -90,
  color: '#D9434B',
  softColor: '#F1A5A8',
  icon: Layers,
  intro: 'What situation is the Receiver in?',
  questions: ['Where are they physically?', 'Are they busy, curious, skeptical, distracted or motivated?', 'How much time do they have?', 'Are they ready to receive the message?']
}, {
  id: 'sender',
  label: 'Sender',
  angle: -45,
  color: '#E27A6B',
  softColor: '#F2BAB1',
  icon: User,
  intro: 'Who is speaking, and how is that sender perceived?',
  questions: ['Is it the company, brand, sales representative, expert, peer, patient or institution?', 'Does the sender appear competent, trustworthy, engaged and honest?']
}, {
  id: 'objective',
  label: 'Objective',
  angle: 0,
  color: '#E89B5A',
  softColor: '#F4CBA3',
  icon: Target,
  intro: 'What should the communication achieve?',
  questions: ['Should it inform, educate, sell, persuade, build trust, start a conversation or capture insight?', "What is the Receiver's reason for engaging?"]
}, {
  id: 'subject',
  label: 'Subject',
  angle: 45,
  color: '#F0B156',
  softColor: '#F7D29C',
  icon: Hash,
  intro: 'What is the communication about?',
  questions: ['Is the subject clear, concrete and relevant?', 'Can it be made vivid with examples, images or comparisons?', 'Does it appeal both logically and emotionally?']
}, {
  id: 'channel',
  label: 'Channel / Medium',
  angle: 90,
  color: '#F2C14E',
  softColor: '#F8DE94',
  icon: Radio,
  intro: 'Where does the Receiver meet the message?',
  questions: ['Is it email, banner, QR code, website, video, congress booth, rep-guided iPad or self-guided mobile Snak?', 'How much attention does the Receiver have in that channel?']
}, {
  id: 'genre',
  label: 'Genre',
  angle: 135,
  color: '#C7CC4E',
  softColor: '#DEE193',
  icon: Film,
  intro: 'What kind of communication is it?',
  questions: ['Is it a quiz, poll, explainer, case, story, challenge, comparison, invitation or teaser?', 'Should the language be formal, clinical, simple, conversational, dramatic or playful?']
}, {
  id: 'flow',
  label: 'Flow',
  angle: 180,
  color: '#A8C45A',
  softColor: '#CFDF9C',
  icon: Workflow,
  intro: 'What journey should the Receiver go through?',
  questions: ['How does it begin?', 'Where is the reveal?', 'Where is the learning moment?', 'Where is the feedback?', 'How should it end?']
}, {
  id: 'form',
  label: 'Form',
  angle: 225,
  color: '#7BAA3A',
  softColor: '#B5CC85',
  icon: Sparkles,
  intro: 'How should the communication feel and look?',
  questions: ['Is it appropriate, clear, correct and vivid?', 'Should it be serious, light, clinical, premium, visual, animated, minimalistic or interactive?']
}];
const receiverContent = {
  intro: 'The Receiver is the core of the model. Every communication choice should be made in relation to the people we want to reach.',
  questions: ['Who are we trying to reach?', 'What do they know?', 'What do they care about?', 'What should they understand, believe, feel or do?']
};
const senderOptions = ['Company', 'Brand', 'Sales representative', 'Medical expert / KOL', 'Peer', 'Patient', 'Neutral educational voice', 'Other'];
const purposeOptions = ['Create awareness', 'Educate', 'Build trust', 'Change perception', 'Start a conversation', 'Drive action', 'Capture insight', 'Other'];
const toneOptions = ['Serious', 'Light', 'Fun', 'Clinical', 'Premium', 'Urgent', 'Helpful', 'Challenging', 'Surprising', 'Reassuring'];
const channelOptions = ['Email', 'Banner', 'Congress booth', 'QR code', 'Rep-guided conversation', 'Self-guided mobile experience', 'Website', 'Sales presentation', 'Other'];
type BriefForm = {
  receiver: string;
  mindset: string;
  shift: string;
  sender: string;
  purpose: string;
  message: string;
  tones: string[];
  channels: string[];
  proof: string;
  nextStep: string;
};
const initialForm: BriefForm = {
  receiver: '',
  mindset: '',
  shift: '',
  sender: '',
  purpose: '',
  message: '',
  tones: [],
  channels: [],
  proof: '',
  nextStep: ''
};
export function RhetoricCompass() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PointId>('receiver');
  const [form, setForm] = useState<BriefForm>(() => {
    if (typeof window === 'undefined') return initialForm;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return initialForm;
      const parsed = JSON.parse(raw);
      return {
        ...initialForm,
        ...parsed
      };
    } catch {
      return initialForm;
    }
  });
  const [showBrief, setShowBrief] = useState(false);
  const [snakNote, setSnakNote] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  // Persist form to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
      setSavedFlash(true);
      const t = setTimeout(() => setSavedFlash(false), 1200);
      return () => clearTimeout(t);
    } catch {
      /* ignore quota errors */
    }
  }, [form]);
  const selectedPoint = points.find(p => p.id === selected);
  const currentLabel = selected === 'receiver' ? 'Receiver' : selectedPoint?.label;
  const currentIntro = selected === 'receiver' ? receiverContent.intro : selectedPoint?.intro;
  const currentQuestions = selected === 'receiver' ? receiverContent.questions : selectedPoint?.questions || [];
  const currentColor = selected === 'receiver' ? '#19A591' : selectedPoint?.color;
  const toggleArrayValue = (key: 'tones' | 'channels', value: string) => {
    setForm(f => {
      const arr = f[key];
      return {
        ...f,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      };
    });
  };
  const handleGenerate = () => {
    setShowBrief(true);
    setSnakNote(false);
    setTimeout(() => {
      document.getElementById('communication-brief')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleSnak = () => {
    setSnakNote(true);
    setTimeout(() => setSnakNote(false), 3500);
  };
  const handleClear = () => {
    if (typeof window !== 'undefined' && !window.confirm('Clear the entire brief? This cannot be undone.')) {
      return;
    }
    setForm(initialForm);
    setShowBrief(false);
  };
  const briefText = buildBriefText(form);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(briefText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };
  const handleDownload = () => {
    const blob = new Blob([briefText], {
      type: 'text/markdown;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `communication-brief-${stamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <>
      <motion.button onClick={() => setOpen(true)} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} className="fixed top-20 right-6 z-40 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-sm font-medium rounded-full flex items-center gap-2 transition-colors" aria-label="Open the Rhetoric Compass strategy tool" data-id="element-213">
        <Compass className="w-4 h-4" data-id="element-214" />
        Strategy Compass
      </motion.button>

      <AnimatePresence data-id="element-215">
        {open && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-solventum-dark/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 md:p-8" onClick={() => setOpen(false)} data-id="element-216">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl max-w-6xl w-full my-auto shadow-2xl overflow-hidden" data-id="element-217">
              {/* Close */}
              <button onClick={() => setOpen(false)} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-solventum-lightTeal hover:bg-solventum-medTeal/30 flex items-center justify-center transition-colors z-10" aria-label="Close" data-id="element-218">
                <X className="w-5 h-5 text-solventum-dark" data-id="element-219" />
              </button>

              {/* Header */}
              <div className="px-6 md:px-12 pt-10 pb-6 border-b border-solventum-gray" data-id="element-220">
                <div className="flex items-center gap-2 text-xs font-semibold text-solventum-teal tracking-[0.2em] uppercase mb-3" data-id="element-221">
                  <Compass className="w-4 h-4" data-id="element-222" />
                  Strategy Tool
                </div>
                <h2 className="font-editorial text-3xl md:text-4xl font-bold text-solventum-dark mb-2" data-id="element-223">
                  The Rhetoric Compass
                </h2>
                <p className="text-solventum-dark/60 max-w-2xl" data-id="element-224">
                  Inspired by the Cicero Pentagram — expanded into a practical
                  communication compass for planning campaigns, Snaks, congress
                  interactions, emails, banners and other healthcare
                  communication. The central principle:{' '}
                  <span className="font-semibold text-solventum-dark" data-id="element-225">
                    start with the Receiver.
                  </span>
                </p>
              </div>

              {/* Section 1 — Visual Compass */}
              <div className="px-6 md:px-12 py-10 bg-gradient-to-b from-white to-solventum-lightTeal/20" data-id="element-226">
                <div className="text-xs font-semibold text-solventum-teal tracking-[0.2em] uppercase mb-2" data-id="element-227">
                  Section 1
                </div>
                <h3 className="font-editorial text-2xl font-bold text-solventum-dark mb-8" data-id="element-228">
                  Explore the compass
                </h3>

                <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start" data-id="element-229">
                  {/* SVG Compass */}
                  <div className="flex justify-center" data-id="element-230">
                    <CompassSvg selected={selected} onSelect={setSelected} data-id="element-231" />
                  </div>

                  {/* Explanation card */}
                  <div className="lg:sticky lg:top-4" data-id="element-232">
                    <AnimatePresence mode="wait" data-id="element-233">
                      <motion.div key={selected} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -8
                  }} transition={{
                    duration: 0.2
                  }} className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)] border border-solventum-gray" data-id="element-234">
                        <div className="flex items-center gap-3 mb-4" data-id="element-235">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{
                        backgroundColor: currentColor
                      }} data-id="element-236">
                            {selected === 'receiver' ? <Users className="w-5 h-5" data-id="element-237" /> : selectedPoint ? <selectedPoint.icon className="w-5 h-5" data-id="element-238" /> : null}
                          </div>
                          <h4 className="font-editorial text-xl font-bold text-solventum-dark" data-id="element-239">
                            {currentLabel}
                          </h4>
                        </div>
                        <p className="text-solventum-dark/80 leading-relaxed text-sm mb-4" data-id="element-240">
                          {currentIntro}
                        </p>
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{
                      color: currentColor
                    }} data-id="element-241">
                          Questions
                        </div>
                        <ul className="space-y-1.5 text-sm text-solventum-dark/75 leading-relaxed" data-id="element-242">
                          {currentQuestions.map(q => <li key={q} className="flex gap-2" data-id="element-243">
                              <span className="mt-1.5 flex-shrink-0 w-1 h-1 rounded-full" style={{
                          backgroundColor: currentColor
                        }} data-id="element-244" />
                              <span data-id="element-245">{q}</span>
                            </li>)}
                        </ul>
                        <div className="mt-5 pt-4 border-t border-solventum-gray text-xs text-solventum-dark/40" data-id="element-246">
                          {selected === 'receiver' ? 'Tap any point on the compass to explore it.' : 'Tap the center to return to Receiver.'}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Section 2 — Form */}
              <div className="px-6 md:px-12 py-10" data-id="element-247">
                <div className="text-xs font-semibold text-solventum-teal tracking-[0.2em] uppercase mb-2" data-id="element-248">
                  Section 2
                </div>
                <h3 className="font-editorial text-2xl font-bold text-solventum-dark mb-2" data-id="element-249">
                  Build your communication brief
                </h3>
                <p className="text-solventum-dark/60 mb-8 max-w-2xl" data-id="element-250">
                  Fill in what you know. The brief will pull it together for
                  your team.
                </p>

                <div className="space-y-6" data-id="element-251">
                  {/* Receiver block */}
                  <FormBlock title="Receiver / Audience" color="#19A591" data-id="element-252">
                    <Textarea placeholder="Example: Busy HCPs who know the category but need a reason to reconsider the brand." value={form.receiver} onChange={v => setForm(f => ({
                  ...f,
                  receiver: v
                }))} data-id="element-253" />
                    <div className="grid md:grid-cols-2 gap-4 mt-3" data-id="element-254">
                      <Textarea label="Current mindset" placeholder="What do they currently know, believe or assume?" value={form.mindset} onChange={v => setForm(f => ({
                    ...f,
                    mindset: v
                  }))} data-id="element-255" />
                      <Textarea label="Desired shift" placeholder="What should they think, feel or do differently afterwards?" value={form.shift} onChange={v => setForm(f => ({
                    ...f,
                    shift: v
                  }))} data-id="element-256" />
                    </div>
                  </FormBlock>

                  <div className="grid md:grid-cols-2 gap-4" data-id="element-257">
                    <FormBlock title="Sender" color="#D9434B" data-id="element-258">
                      <Select options={senderOptions} value={form.sender} onChange={v => setForm(f => ({
                    ...f,
                    sender: v
                  }))} placeholder="Choose a sender voice" data-id="element-259" />
                    </FormBlock>
                    <FormBlock title="Purpose" color="#E27A6B" data-id="element-260">
                      <Select options={purposeOptions} value={form.purpose} onChange={v => setForm(f => ({
                    ...f,
                    purpose: v
                  }))} placeholder="Choose a purpose" data-id="element-261" />
                    </FormBlock>
                  </div>

                  <FormBlock title="Core message" color="#E89B5A" data-id="element-262">
                    <Textarea placeholder="What is the one main point?" value={form.message} onChange={v => setForm(f => ({
                  ...f,
                  message: v
                }))} data-id="element-263" />
                  </FormBlock>

                  <FormBlock title="Form / Tone" color="#F2C14E" data-id="element-264">
                    <ChipGroup options={toneOptions} selected={form.tones} onToggle={v => toggleArrayValue('tones', v)} data-id="element-265" />
                  </FormBlock>

                  <FormBlock title="Channel / Situation" color="#A8C45A" data-id="element-266">
                    <ChipGroup options={channelOptions} selected={form.channels} onToggle={v => toggleArrayValue('channels', v)} data-id="element-267" />
                  </FormBlock>

                  <FormBlock title="Proof / Content source" color="#7BAA3A" data-id="element-268">
                    <Textarea placeholder="What approved claims, references, data or content can be used?" value={form.proof} onChange={v => setForm(f => ({
                  ...f,
                  proof: v
                }))} data-id="element-269" />
                  </FormBlock>

                  <FormBlock title="Desired next step" color="#01332B" data-id="element-270">
                    <Textarea placeholder="What should the receiver do next?" value={form.nextStep} onChange={v => setForm(f => ({
                  ...f,
                  nextStep: v
                }))} data-id="element-271" />
                  </FormBlock>
                </div>

                {/* Action buttons */}
                <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-272">
                  <button onClick={handleGenerate} className="px-6 py-3 bg-solventum-green hover:bg-solventum-medTeal text-solventum-dark font-bold rounded-full flex items-center justify-center gap-2 transition-colors" data-id="element-273">
                    <FileDown className="w-4 h-4" data-id="element-274" />
                    Generate Communication Brief
                  </button>
                  <button onClick={handleSnak} className="px-6 py-3 bg-white border-2 border-solventum-green text-solventum-dark hover:bg-solventum-lightTeal/40 font-bold rounded-full flex items-center justify-center gap-2 transition-colors" data-id="element-275">
                    <ArrowRight className="w-4 h-4" data-id="element-276" />
                    Create Snak Direction
                  </button>
                  <button onClick={handleClear} className="sm:ml-auto px-4 py-2 text-solventum-dark/50 hover:text-solventum-dark text-sm font-medium rounded-full flex items-center justify-center gap-1.5 transition-colors" aria-label="Clear the form" data-id="element-277">
                    <Trash2 className="w-3.5 h-3.5" data-id="element-278" />
                    Clear
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-solventum-dark/45" data-id="element-279">
                  <AnimatePresence mode="wait" data-id="element-280">
                    {savedFlash ? <motion.span key="saved" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-1.5 text-solventum-teal" data-id="element-281">
                        <Check className="w-3 h-3" data-id="element-282" />
                        Saved to your browser
                      </motion.span> : <motion.span key="idle" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} data-id="element-283">
                        Your inputs auto-save to this browser.
                      </motion.span>}
                  </AnimatePresence>
                </div>

                <AnimatePresence data-id="element-284">
                  {snakNote && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} className="mt-4 text-sm text-solventum-teal flex items-center gap-2" data-id="element-285">
                      <Check className="w-4 h-4" data-id="element-286" />
                      Snak direction noted — full output coming soon.
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Output — Communication Brief */}
              <AnimatePresence data-id="element-287">
                {showBrief && <motion.div id="communication-brief" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.4
            }} className="px-6 md:px-12 py-10 bg-solventum-lightTeal/30 border-t border-solventum-gray" data-id="element-288">
                    <div className="bg-white rounded-2xl shadow-[0_20px_50px_-20px_rgba(0,0,0,0.2)] p-8 md:p-10 border border-solventum-gray" data-id="element-289">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6" data-id="element-290">
                        <div data-id="element-291">
                          <div className="flex items-center gap-2 text-xs font-semibold text-solventum-teal tracking-[0.2em] uppercase mb-3" data-id="element-292">
                            <Check className="w-4 h-4" data-id="element-293" />
                            Generated Brief
                          </div>
                          <h3 className="font-editorial text-2xl font-bold text-solventum-dark" data-id="element-294">
                            Communication Brief
                          </h3>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-295">
                          <button onClick={handleCopy} className="px-3 py-2 text-xs font-semibold rounded-full border border-solventum-gray hover:border-solventum-teal text-solventum-dark flex items-center gap-1.5 transition-colors" data-id="element-296">
                            {copied ? <>
                                <Check className="w-3.5 h-3.5 text-solventum-teal" data-id="element-297" />
                                Copied
                              </> : <>
                                <Copy className="w-3.5 h-3.5" data-id="element-298" />
                                Copy
                              </>}
                          </button>
                          <button onClick={handleDownload} className="px-3 py-2 text-xs font-semibold rounded-full bg-solventum-dark hover:bg-solventum-teal text-white flex items-center gap-1.5 transition-colors" data-id="element-299">
                            <Download className="w-3.5 h-3.5" data-id="element-300" />
                            Download .md
                          </button>
                        </div>
                      </div>
                      <div className="-mt-2 mb-8" data-id="element-301" />
                      <div className="grid md:grid-cols-2 gap-x-10 gap-y-7" data-id="element-302">
                        <BriefSection title="Audience insight" accent="#19A591" content={buildAudienceInsight(form)} data-id="element-303" />
                        <BriefSection title="Strategic purpose" accent="#E27A6B" content={form.purpose || '—'} data-id="element-304" />
                        <BriefSection title="Recommended sender voice" accent="#D9434B" content={form.sender || '—'} data-id="element-305" />
                        <BriefSection title="Core message direction" accent="#E89B5A" content={form.message || '—'} data-id="element-306" />
                        <BriefSection title="Recommended tone" accent="#F2C14E" content={form.tones.length ? form.tones.join(', ') : '—'} data-id="element-307" />
                        <BriefSection title="Best-fit channel context" accent="#A8C45A" content={form.channels.length ? form.channels.join(', ') : '—'} data-id="element-308" />
                        <BriefSection title="Proof needed" accent="#7BAA3A" content={form.proof || '—'} data-id="element-309" />
                        <BriefSection title="Suggested next step" accent="#01332B" content={form.nextStep || '—'} data-id="element-310" />
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function buildBriefText(f: BriefForm) {
  const line = (label: string, value: string) => `## ${label}\n${value && value.trim() ? value : '—'}\n`;
  return ['# Communication Brief', `_Generated ${new Date().toLocaleDateString()}_\n`, line('Audience insight', buildAudienceInsight(f)), line('Strategic purpose', f.purpose), line('Recommended sender voice', f.sender), line('Core message direction', f.message), line('Recommended tone', f.tones.length ? f.tones.join(', ') : ''), line('Best-fit channel context', f.channels.length ? f.channels.join(', ') : ''), line('Proof needed', f.proof), line('Suggested next step', f.nextStep)].join('\n');
}
function buildAudienceInsight(f: BriefForm) {
  const parts: string[] = [];
  if (f.receiver) parts.push(f.receiver);
  if (f.mindset) parts.push(`Currently: ${f.mindset}`);
  if (f.shift) parts.push(`Shift to: ${f.shift}`);
  return parts.length ? parts.join(' · ') : '—';
}
// ── Compass SVG ────────────────────────────────────────────────
function CompassSvg({
  selected,
  onSelect
}: {
  selected: PointId;
  onSelect: (id: PointId) => void;
}) {
  const cx = 200;
  const cy = 200;
  const centerR = 68;
  const apexR = 178;
  const baseR = 74;
  const halfBase = 0.32; // radians — sized for 8 points at 45° intervals
  return <svg viewBox="-110 -50 620 500" width="100%" style={{
    maxWidth: 600
  }} aria-label="Rhetoric Compass — 6 strategic points around the receiver" data-id="element-311">
      <defs data-id="element-312">
        <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-313">
          <feGaussianBlur stdDeviation="3" data-id="element-314" />
        </filter>
      </defs>

      {/* Triangles */}
      {points.map(p => {
      const isActive = selected === p.id;
      const rad = p.angle * Math.PI / 180;
      const apexX = cx + Math.cos(rad) * apexR;
      const apexY = cy + Math.sin(rad) * apexR;
      const baseLeftX = cx + Math.cos(rad - halfBase) * baseR;
      const baseLeftY = cy + Math.sin(rad - halfBase) * baseR;
      const baseRightX = cx + Math.cos(rad + halfBase) * baseR;
      const baseRightY = cy + Math.sin(rad + halfBase) * baseR;
      // Label position (slightly outside apex)
      const labelR = apexR + 24;
      const labelX = cx + Math.cos(rad) * labelR;
      const labelY = cy + Math.sin(rad) * labelR;
      const isLeftSide = Math.cos(rad) < -0.3;
      const isRightSide = Math.cos(rad) > 0.3;
      const textAnchor = isLeftSide ? 'end' : isRightSide ? 'start' : 'middle';
      return <g key={p.id} style={{
        cursor: 'pointer'
      }} onClick={() => onSelect(p.id)} data-id="element-315">
            <motion.polygon points={`${apexX},${apexY} ${baseLeftX},${baseLeftY} ${baseRightX},${baseRightY}`} fill={p.color} stroke="#ffffff" strokeWidth={2} strokeLinejoin="round" initial={false} animate={{
          scale: isActive ? 1.06 : 1,
          opacity: selected === 'receiver' || isActive ? 1 : 0.55
        }} transition={{
          duration: 0.25
        }} style={{
          transformOrigin: `${cx}px ${cy}px`
        }} data-id="element-316" />
            <text x={labelX} y={labelY} textAnchor={textAnchor} dominantBaseline="middle" className="select-none" style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: isActive ? 700 : 600,
          fontSize: 13,
          fill: isActive ? p.color : '#01332B'
        }} data-id="element-317">
              {p.label.includes(' / ') ? <>
                  <tspan x={labelX} dy="-0.55em" data-id="element-318">
                    {p.label.split(' / ')[0]}
                  </tspan>
                  <tspan x={labelX} dy="1.1em" data-id="element-319">
                    / {p.label.split(' / ')[1]}
                  </tspan>
                </> : p.label}
            </text>
          </g>;
    })}

      {/* Center receiver circle */}
      <g style={{
      cursor: 'pointer'
    }} onClick={() => onSelect('receiver')} data-id="element-320">
        <motion.circle cx={cx} cy={cy} r={centerR} fill="#19A591" stroke="#ffffff" strokeWidth={4} initial={false} animate={{
        scale: selected === 'receiver' ? 1.06 : 1
      }} transition={{
        duration: 0.25
      }} style={{
        transformOrigin: `${cx}px ${cy}px`
      }} data-id="element-321" />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" style={{
        fontFamily: 'Fraunces, serif',
        fontWeight: 700,
        fontSize: 22,
        fill: '#ffffff'
      }} data-id="element-322">
          Receiver
        </text>
      </g>
    </svg>;
}
// ── Form atoms ─────────────────────────────────────────────────
function FormBlock({
  title,
  color,
  children
}: {
  title: string;
  color: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white rounded-xl border border-solventum-gray p-5" data-id="element-323">
      <div className="flex items-center gap-2 mb-3" data-id="element-324">
        <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: color
      }} data-id="element-325" />
        <div className="text-xs font-bold tracking-[0.18em] uppercase text-solventum-dark/70" data-id="element-326">
          {title}
        </div>
      </div>
      {children}
    </div>;
}
function Textarea({
  label,
  placeholder,
  value,
  onChange
}: {
  label?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <label className="block" data-id="element-327">
      {label && <div className="text-xs font-semibold text-solventum-dark/60 mb-1.5" data-id="element-328">
          {label}
        </div>}
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className="w-full px-3 py-2.5 text-sm bg-white border border-solventum-gray rounded-lg text-solventum-dark placeholder-solventum-dark/35 focus:outline-none focus:ring-2 focus:ring-solventum-green/60 focus:border-solventum-green transition-colors resize-none" data-id="element-329" />
    </label>;
}
function Select({
  options,
  value,
  onChange,
  placeholder
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-white border border-solventum-gray rounded-lg text-solventum-dark focus:outline-none focus:ring-2 focus:ring-solventum-green/60 focus:border-solventum-green transition-colors" data-id="element-330">
      <option value="" data-id="element-331">{placeholder}</option>
      {options.map(opt => <option key={opt} value={opt} data-id="element-332">
          {opt}
        </option>)}
    </select>;
}
function ChipGroup({
  options,
  selected,
  onToggle
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return <div className="flex flex-wrap gap-2" data-id="element-333">
      {options.map(opt => {
      const isOn = selected.includes(opt);
      return <button key={opt} type="button" onClick={() => onToggle(opt)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${isOn ? 'bg-solventum-green border-solventum-green text-solventum-dark' : 'bg-white border-solventum-gray text-solventum-dark/70 hover:border-solventum-teal'}`} data-id="element-334">
            {opt}
          </button>;
    })}
    </div>;
}
function BriefSection({
  title,
  accent,
  content
}: {
  title: string;
  accent: string;
  content: string;
}) {
  return <div data-id="element-335">
      <div className="flex items-center gap-2 mb-2" data-id="element-336">
        <span className="w-6 h-[2px] rounded-full" style={{
        backgroundColor: accent
      }} data-id="element-337" />
        <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-solventum-dark/60" data-id="element-338">
          {title}
        </div>
      </div>
      <div className="text-solventum-dark leading-relaxed text-sm whitespace-pre-wrap" data-id="element-339">
        {content}
      </div>
    </div>;
}